import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { CompetitorDaysData, DEFAULT_OUTLIER_CONFIG, PmcPriceMode, PriceOption, SetupIPADadosDeConcorrencia } from '../SetupIPADadosDeConcorrenciatypes';
import { ConfigFormProps, FormValues, schema } from '../interfaces';
import { FormFooter } from './FormFooter';
import { FormHeader } from './FormHeader';
import { OutliersSection } from './OutliersSection';
import { PmcPriceModeSection } from './PmcPriceModeSection';
import { PriceOptionsSection } from './PriceOptionsSection';
import { PricePreferenceSection } from './PricePreferenceSection';

const getFormValues = (data?: SetupIPADadosDeConcorrencia): FormValues => ({
  id: data?.id ?? null,
  competitor_days: data?.competitor_days ?? 1,
  suppliers_competitor_days: data?.suppliers_competitor_days ?? 1,
  price_option: data?.competitor_data?.price_option ?? PriceOption.REGULAR,
  other_prices: data?.competitor_data?.other_prices ?? [],
  pmc_price_mode: data?.competitor_data?.pmc_price_mode ?? PmcPriceMode.NOT_EXCLUDE,
  outlier_config: data?.outlier_config ?? DEFAULT_OUTLIER_CONFIG,
});

const ConfigForm = ({ data, createConfig, updateConfig, isLoading = false, isSaving = false }: ConfigFormProps) => {
  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: getFormValues(),
  });

  const priceOption = useWatch({ control, name: 'price_option' });

  useEffect(() => {
    if (data) reset(getFormValues(data));
  }, [data, reset]);

  const onSubmit = async (formData: FormValues) => {
    const payload: CompetitorDaysData = {
      competitor_days: formData.competitor_days,
      suppliers_competitor_days: formData.suppliers_competitor_days,
      competitor_data: {
        price_option: formData.price_option,
        other_prices: formData.price_option === PriceOption.REGULAR_AND_OTHERS ? formData.other_prices : undefined,
        pmc_price_mode: formData.pmc_price_mode,
      },
      outlier_config: {
        active: formData.outlier_config.active,
        max_limit: formData.outlier_config.max_limit ?? DEFAULT_OUTLIER_CONFIG.max_limit,
        min_limit: formData.outlier_config.min_limit ?? DEFAULT_OUTLIER_CONFIG.min_limit,
      },
    };

    const savedConfig = formData.id ? await updateConfig(payload) : await createConfig(payload);

    reset({
      ...formData,
      id: savedConfig.id ?? formData.id,
      outlier_config: payload.outlier_config,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} onReset={() => reset()}>
      <FormHeader />
      <PricePreferenceSection control={control} />
      <PriceOptionsSection control={control} showOtherPrices={priceOption === PriceOption.REGULAR_AND_OTHERS} />
      <PmcPriceModeSection control={control} />
      <OutliersSection control={control} isLoading={isLoading} />
      <FormFooter isSaving={isSaving} />
    </form>
  );
};

export default ConfigForm;
